/**
 * Custom date picker. ISO-only (YYYY-MM-DD) on the wire so it composes with
 * Zod date strings, Mongoose dates, and the rest of the API. UI shows the
 * date in the user's locale (e.g. "May 21, 2026").
 *
 * - Trigger looks like a 40px input with a calendar lead icon and a clear ×.
 * - Popover calendar starts the week on Monday, dims days outside the
 *   current month, outlines today, fills the selected day with the accent.
 * - Min/max are honoured (used for range filters).
 * - Keyboard: ↓/Space/Enter to open, ←/→/↑/↓ to move by day/week, Enter to
 *   commit the focused date, Esc to close.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { Field } from './Field';
import { Icon } from './Icon';

interface Props {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  id?: string;
  ariaLabel?: string;
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmt(d: Date): string {
  return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: '2-digit' });
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function daysGrid(view: Date): Date[] {
  const first = startOfMonth(view);
  // getDay(): 0=Sun..6=Sat → convert to 0=Mon..6=Sun.
  const dow = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - dow);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function DatePicker({
  value, onChange,
  label, hint, error,
  placeholder = 'Choisir une date',
  disabled, min, max, id, ariaLabel,
}: Props) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const parsed = useMemo(() => parseISO(value), [value]);
  const minD = useMemo(() => parseISO(min), [min]);
  const maxD = useMemo(() => parseISO(max), [max]);

  const [view, setView] = useState<Date>(() => startOfMonth(parsed ?? new Date()));
  useEffect(() => {
    if (open) setView(startOfMonth(parsed ?? new Date()));
  }, [open, parsed]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const grid = useMemo(() => daysGrid(view), [view]);
  const today = useMemo(() => new Date(), []);
  const monthLabel = view.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  function inRange(d: Date): boolean {
    if (minD && d < minD) return false;
    if (maxD && d > maxD) return false;
    return true;
  }

  function pick(d: Date): void {
    if (!inRange(d)) return;
    onChange(toISO(d));
    setOpen(false);
    triggerRef.current?.focus();
  }

  function clear(e: ReactMouseEvent<HTMLSpanElement>): void {
    e.stopPropagation();
    onChange('');
  }

  function shift(amt: number): void {
    const base = parsed ?? today;
    const next = new Date(base);
    next.setDate(base.getDate() + amt);
    if (!inRange(next)) return;
    onChange(toISO(next));
    setView(startOfMonth(next));
  }

  function handleKey(e: KeyboardEvent<HTMLDivElement>): void {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); shift(-1); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); shift(1);  return; }
    if (e.key === 'ArrowUp')    { e.preventDefault(); shift(-7); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); shift(7);  return; }
    if (e.key === 'Enter')      { e.preventDefault(); if (parsed) pick(parsed); return; }
    if (e.key === 'Tab')        { setOpen(false); return; }
  }

  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <div
        ref={containerRef}
        className={`date-picker${open ? ' is-open' : ''}${error ? ' err-state' : ''}`}
        onKeyDown={handleKey}
      >
        <button
          ref={triggerRef}
          id={inputId}
          type="button"
          className="date-trigger"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={ariaLabel ?? label ?? placeholder}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
        >
          <span className="lead" aria-hidden><Icon name="calendar" size={16} /></span>
          <span className={`date-label${parsed ? '' : ' placeholder'}`}>
            {parsed ? fmt(parsed) : placeholder}
          </span>
          {parsed && !disabled && (
            <span
              className="clear-btn"
              role="button"
              tabIndex={-1}
              aria-label="Effacer la date"
              onClick={clear}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Icon name="x" size={12} />
            </span>
          )}
        </button>

        {open && (
          <div className="menu-pop date-popover" role="dialog" aria-label="Choisir une date">
            <div className="cal-header">
              <button
                type="button"
                className="cal-nav"
                aria-label="Mois précédent"
                onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))}
              >
                <Icon name="chevron-left" size={16} />
              </button>
              <span className="cal-month">{monthLabel}</span>
              <button
                type="button"
                className="cal-nav"
                aria-label="Mois suivant"
                onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))}
              >
                <Icon name="chevron-right" size={16} />
              </button>
            </div>

            <div className="cal-weekdays">
              {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
            </div>

            <div className="cal-grid">
              {grid.map((d) => {
                const otherMonth = d.getMonth() !== view.getMonth();
                const isToday    = sameDay(d, today);
                const isSelected = !!parsed && sameDay(d, parsed);
                const disabledD  = !inRange(d);
                const cls = [
                  'cal-day',
                  otherMonth ? 'other' : '',
                  isToday ? 'today' : '',
                  isSelected ? 'selected' : '',
                  disabledD ? 'disabled' : '',
                ].filter(Boolean).join(' ');
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    className={cls}
                    disabled={disabledD}
                    onClick={() => pick(d)}
                    aria-pressed={isSelected}
                    aria-label={fmt(d)}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="cal-footer">
              <button type="button" className="cal-link" onClick={() => pick(today)}>Aujourd'hui</button>
              {parsed && (
                <button type="button" className="cal-link" onClick={() => { onChange(''); setOpen(false); }}>
                  Effacer
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Field>
  );
}
